import React, { useState, useEffect } from 'react';
import Modal from '../Modal';
import { Remorque, RemorqueCreate, Camion } from '../../types';
import weighingService from '../../services/weighing';
import SearchableSelect from '../SearchableSelect';
import AddButton from '../AddButton';

interface AddTrailerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (trailer: RemorqueCreate) => void;
}

const AddTrailerModal: React.FC<AddTrailerModalProps> = ({ isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState<RemorqueCreate>({
    immatriculation: undefined,
    marque: '',
    modele: '',
    capacite: 0,
    proprietaire: '',
    longueur: 0,
    largeur: 0,
    hauteur: 0,
    tare_theorique: undefined,
    statut: 'actif',
    notes: '',
    camion_associe: null
  });

  const [errors, setErrors] = useState<Partial<Record<keyof RemorqueCreate, string>>>({});
  const [vehicles, setVehicles] = useState<Camion[]>([]);
  const [loadingVehicles, setLoadingVehicles] = useState(false);
  const [showAddVehicleModal, setShowAddVehicleModal] = useState(false);
  const [newVehicleData, setNewVehicleData] = useState({
    immatriculation: '',
    marque: '',
    modele: '',
    capacite: 0,
    nombre_passages: 2,
    proprietaire: ''
  });


  // Charger la liste des véhicules disponibles
  useEffect(() => {
    if (isOpen) {
      loadVehicles();
    }
  }, [isOpen]);

  const loadVehicles = async () => {
    setLoadingVehicles(true);
    try {
      // page_size élevé pour récupérer tous les résultats
      const response = await weighingService.getCamions({ page_size: 1000 });
      console.log(response);
      
      if (response && response.results) {
        setVehicles(response.results || []);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des véhicules:', error);
    } finally {
      setLoadingVehicles(false);
    }
  };

  const handleAddVehicle = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await weighingService.createCamion(newVehicleData);
      
      // Recharger la liste des véhicules
      await loadVehicles();
      // Sélectionner automatiquement le nouveau véhicule
      if (response?.data?.id) {
        setFormData(prev => ({ ...prev, camion_associe: response.data!.id }));
      }
      setShowAddVehicleModal(false);
      // Réinitialiser le formulaire du véhicule
      setNewVehicleData({
        immatriculation: '',
        marque: '',
        modele: '',
        capacite: 0,
        nombre_passages: 2,
        proprietaire: ''
      });
    } catch (error) {
      console.error('Erreur lors de l\'ajout du véhicule:', error);
    }
  };

  const handleNewVehicleInputChange = (field: string, value: any) => {
    setNewVehicleData(prev => ({ ...prev, [field]: value }));
  };

  const handleInputChange = (field: keyof RemorqueCreate, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof Remorque, string>> = {};

    // L'immatriculation est maintenant optionnelle

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Envoyer undefined si l'immatriculation est vide
      const dataToSave = {
        ...formData,
        immatriculation: formData.immatriculation?.trim() || undefined
      };
      onSave(dataToSave);
      onClose();
      resetForm();
    }
  };

  const resetForm = () => {
    setFormData({
      immatriculation: undefined,
      marque: '',
      modele: '',
      capacite: 0,
      proprietaire: '',
      longueur: 0,
      largeur: 0,
      hauteur: 0,
      tare_theorique: undefined,
      statut: 'actif',
      notes: '',
      camion_associe: null
    });
    setErrors({});
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Ajouter une remorque" size="lg">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Immatriculation et Marque */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Immatriculation
            </label>
            <input
              type="text"
              value={formData.immatriculation ?? ''}
              onChange={(e) => handleInputChange('immatriculation', e.target.value.toUpperCase())}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                errors.immatriculation ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="12AB345CD"
            />
            {errors.immatriculation && (
              <p className="mt-1 text-sm text-red-600">{errors.immatriculation}</p>
            )}
            <p className="mt-1 text-sm text-gray-500">Format Côte d'Ivoire : 12AB345CD (optionnel)</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Marque 
            </label>
            <input
              type="text"
              value={formData.marque || ''}
              onChange={(e) => handleInputChange('marque', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                errors.marque ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Schmitz, Krone, Kögel..."
            />
            {errors.marque && (
              <p className="mt-1 text-sm text-red-600">{errors.marque}</p>
            )}
          </div>
        </div>

        {/* Modèle et Capacité */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Modèle 
            </label>
            <input
              type="text"
              value={formData.modele || ''}
              onChange={(e) => handleInputChange('modele', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                errors.modele ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Cargo, Curtainsider, Box..."
            />
            {errors.modele && (
              <p className="mt-1 text-sm text-red-600">{errors.modele}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Capacité (kg) 
            </label>
            <input
              type="number"
              value={formData.capacite || ''}
              onChange={(e) => handleInputChange('capacite', parseInt(e.target.value) || 0)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                errors.capacite ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="24000"
              min="0"
            />
            {errors.capacite && (
              <p className="mt-1 text-sm text-red-600">{errors.capacite}</p>
            )}
          </div>
        </div>

        {/* Propriétaire et Tare théorique */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Propriétaire
            </label>
            <input
              type="text"
              value={formData.proprietaire || ''}
              onChange={(e) => handleInputChange('proprietaire', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                errors.proprietaire ? 'border-red-300' : 'border-gray-300'
              }`}
              placeholder="Nom du propriétaire"
            />
            {errors.proprietaire && (
              <p className="mt-1 text-sm text-red-600">{errors.proprietaire}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tare théorique (kg)
            </label>
            <input
              type="number"
              value={formData.tare_theorique || ''}
              onChange={(e) => handleInputChange('tare_theorique', e.target.value ? parseInt(e.target.value) : undefined)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              placeholder="3500"
              min="0"
            />
          </div>
        </div>

        {/* Véhicule associé */}
        <div className='flex flex-col'>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Véhicule associé (optionnel)
            </label>
            
          </div>
          
          <div className="space-y-2">
            <div className="flex items-center space-x-2 justify-start w-full">
              <SearchableSelect
                value={formData.camion_associe?.toString() || ''}
                onChange={(value) => handleInputChange('camion_associe', value ? parseInt(value) : null)}
                options={vehicles.map(vehicle => ({
                  value: vehicle.id.toString(),
                  label: `${vehicle.immatriculation} - ${vehicle.marque} ${vehicle.modele}`
                }))}
                placeholder="Sélectionner un véhicule"
                disabled={loadingVehicles}
                className='w-full'
              />
              <AddButton
                onClick={() => setShowAddVehicleModal(true)}
                tooltip="Ajouter un nouveau véhicule"
              />
            </div>
            
            {formData.camion_associe && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Véhicule sélectionné :</strong> {vehicles.find(v => v.id === formData.camion_associe)?.immatriculation} - {vehicles.find(v => v.id === formData.camion_associe)?.marque} {vehicles.find(v => v.id === formData.camion_associe)?.modele}
                </p>
              </div>
            )}
          </div>
          
          <p className="mt-1 text-sm text-gray-500">
            Sélectionnez un véhicule existant ou ajoutez-en un nouveau
          </p>
        </div>

        {/* Dimensions */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Dimensions (mètres)</h3>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Longueur
              </label>
              <input
                type="number"
                step="0.1"
                value={formData.longueur || ''}
                onChange={(e) => handleInputChange('longueur', parseFloat(e.target.value) || 0)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                  errors.longueur ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="13.6"
                min="0"
              />
              {errors.longueur && (
                <p className="mt-1 text-sm text-red-600">{errors.longueur}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Largeur
              </label>
              <input
                type="number"
                step="0.1"
                value={formData.largeur || ''}
                onChange={(e) => handleInputChange('largeur', parseFloat(e.target.value) || 0)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                  errors.largeur ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="2.55"
                min="0"
              />
              {errors.largeur && (
                <p className="mt-1 text-sm text-red-600">{errors.largeur}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Hauteur 
              </label>
              <input
                type="number"
                step="0.1"
                value={formData.hauteur || ''}
                onChange={(e) => handleInputChange('hauteur', parseFloat(e.target.value) || 0)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                  errors.hauteur ? 'border-red-300' : 'border-gray-300'
                }`}
                placeholder="2.7"
                min="0"
              />
              {errors.hauteur && (
                <p className="mt-1 text-sm text-red-600">{errors.hauteur}</p>
              )}
            </div>
          </div>
        </div>

        {/* Statut et Notes */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Statut
            </label>
            <select
              value={formData.statut}
              onChange={(e) => handleInputChange('statut', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            >
              <option value="actif">Actif</option>
              <option value="maintenance">En maintenance</option>
              <option value="hors-service">Hors service</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes
            </label>
            <textarea
              value={formData.notes || ''}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              placeholder="Notes additionnelles..."
              rows={3}
            />
          </div>
        </div>

        {/* Boutons d'action */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Annuler
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
          >
            Ajouter la remorque
          </button>
        </div>
      </form>
      
      {/* Modal d'ajout de véhicule */}
      {showAddVehicleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Ajouter un nouveau véhicule</h3>
              <button
                onClick={() => setShowAddVehicleModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleAddVehicle} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Immatriculation <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={newVehicleData.immatriculation}
                  onChange={(e) => handleNewVehicleInputChange('immatriculation', e.target.value.toUpperCase())}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="12AB345CD"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Marque 
                  </label>
                  <input
                    type="text"
                    value={newVehicleData.marque}
                    onChange={(e) => handleNewVehicleInputChange('marque', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Mercedes"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Modèle 
                  </label>
                  <input
                    type="text"
                    value={newVehicleData.modele}
                    onChange={(e) => handleNewVehicleInputChange('modele', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Actros"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Capacité (kg) 
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={newVehicleData.capacite || ''}
                    onChange={(e) => handleNewVehicleInputChange('capacite', parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="24000"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre de places
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={newVehicleData.nombre_passages || ''}
                    onChange={(e) => handleNewVehicleInputChange('nombre_passages', parseInt(e.target.value) || 2)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="2"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Propriétaire
                </label>
                <input
                  type="text"
                  value={newVehicleData.proprietaire}
                  onChange={(e) => handleNewVehicleInputChange('proprietaire', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  placeholder="Nom du propriétaire"
                />
              </div>
              
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddVehicleModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                >
                  Ajouter le véhicule
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default AddTrailerModal;
